/**
 * skeleton-composites.tsx
 *
 * Higher-level skeleton building blocks. Each composite mirrors a real UI
 * pattern so the loading state is shape-matched to the settled state — users
 * see a recognisable layout, not generic grey boxes.
 *
 * Performance contract (same as Skeleton):
 *  - Pure CSS shimmer, no JS timers, no network requests.
 *  - Shimmer is motion-safe-gated; reduced-motion users get static blocks.
 *  - No additional bundle overhead: composites are tree-shaken individually.
 *
 * Progress bars: DISABLED. Our TanStack Query fetches execute atomically
 * (no streaming chunks, no partial progress metric). A progress bar would
 * be fake — it would lie about completion %. Enable only if/when the sync
 * pipeline exposes real byte-level throughput for a specific operation.
 */

import type { HTMLAttributes } from 'react';
import { cn } from '../lib/cn';
import { Card } from './card';
import { TableRow, TableCell } from './table';
import { Skeleton } from './skeleton';

// ─── Text lines ──────────────────────────────────────────────────────────────

/**
 * A block of skeleton text lines. Widths vary to mimic natural prose and
 * avoid the "uniform bars" look.
 *
 * @param lines  Number of lines to render (default: 3).
 */
export function SkeletonText({
  lines = 3,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  // Cascade of widths: full → 5/6 → 4/6 → 3/4 → 2/3 → 3/5 → repeat
  const widths = ['w-full', 'w-5/6', 'w-4/6', 'w-3/4', 'w-2/3', 'w-3/5'];
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={cn('h-4', widths[i % widths.length])} />
      ))}
    </div>
  );
}

// ─── KPI metric card ─────────────────────────────────────────────────────────

/**
 * Single KPI/metric card skeleton.
 *
 * Matches the ubiquitous 3-row card layout found in Clarity, GA4, Google Ads,
 * and Marketing Dashboard pages:
 *   [label line — caption]
 *   [value — h1 mono]
 *   [delta badge — caption] (optional)
 *
 * @param showDelta  Whether to render the delta/trend line (default: false).
 */
export function SkeletonKpi({
  showDelta = false,
  className,
}: {
  showDelta?: boolean;
  className?: string;
}) {
  return (
    <Card padding="none" className={cn('p-4', className)}>
      {/* label */}
      <Skeleton className="h-3 w-20" />
      {/* big metric value */}
      <Skeleton className="mt-2 h-8 w-28" />
      {/* delta — rendered only when showDelta=true to match real card height */}
      {showDelta && <Skeleton className="mt-2 h-3 w-16" />}
    </Card>
  );
}

// ─── Generic card ─────────────────────────────────────────────────────────────

/**
 * Generic card skeleton with an optional title bar and a rectangular body.
 *
 * Use for chart cards, summary panels, or any card with a title + content area.
 *
 * @param bodyHeight  Tailwind height class for the body area (default: 'h-48').
 * @param showTitle   Whether to render the title bar skeleton (default: true).
 */
export function SkeletonCard({
  bodyHeight = 'h-48',
  showTitle = true,
  className,
}: {
  bodyHeight?: string;
  showTitle?: boolean;
  className?: string;
}) {
  return (
    <Card padding="none" className={cn('p-6', className)}>
      {showTitle && <Skeleton className="mb-4 h-5 w-44" />}
      <Skeleton className={cn('w-full', bodyHeight)} />
    </Card>
  );
}

// ─── Chart area ───────────────────────────────────────────────────────────────

/**
 * Inline chart-area skeleton. Drop this inside an existing card to replace
 * just the chart canvas while data loads.
 *
 * @param height  Tailwind height class (default: 'h-64').
 */
export function SkeletonChartArea({
  height = 'h-64',
  className,
}: {
  height?: string;
  className?: string;
}) {
  return <Skeleton className={cn('w-full', height, className)} />;
}

// ─── Table body ───────────────────────────────────────────────────────────────

/**
 * Table-body row set to replace `<TableBody>` while data loads.
 *
 * Renders `rows` × `cols` skeleton cells. The first column is intentionally
 * narrower to suggest a name/label field; the rest fill their cell width.
 *
 * Usage:
 * ```tsx
 * <Table>
 *   <TableHeader>...</TableHeader>
 *   <TableBody>
 *     {isLoading ? <SkeletonTableBody rows={6} cols={5} /> : <RealRows />}
 *   </TableBody>
 * </Table>
 * ```
 *
 * @param rows  Number of placeholder rows (default: 5).
 * @param cols  Number of cells per row (default: 4).
 */
export function SkeletonTableBody({
  rows = 5,
  cols = 4,
}: {
  rows?: number;
  cols?: number;
}) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <TableRow key={i}>
          {Array.from({ length: cols }, (_, j) => (
            <TableCell key={j}>
              <Skeleton className={cn('h-4', j === 0 ? 'w-32' : 'w-full')} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

// ─── Page-level default scaffold ─────────────────────────────────────────────

/**
 * Full-page default loading scaffold: 4 KPI cards + 1 chart card.
 *
 * Suitable for any marketing / analytics page that hasn't defined a custom
 * skeleton yet. Pass as the `skeleton` prop of `PageFrame`:
 *
 * ```tsx
 * <PageFrame skeleton={isLoading && <PageSkeletonDefault />}>
 *   ...real content...
 * </PageFrame>
 * ```
 */
export function PageSkeletonDefault({
  kpiCount = 4,
  chartHeight = 'h-64',
  className,
}: {
  kpiCount?: number;
  chartHeight?: string;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {Array.from({ length: kpiCount }, (_, i) => (
          <SkeletonKpi key={i} />
        ))}
      </div>
      <SkeletonCard bodyHeight={chartHeight} />
    </div>
  );
}

// ─── Stat row (label + value pairs in a horizontal strip) ────────────────────

/**
 * A horizontal strip of inline stat pills — e.g. the "3 campaigns · 2 paused"
 * row found in summary headers.
 *
 * @param count  Number of stat pills to render (default: 3).
 */
export function SkeletonStatRow({
  count = 3,
  className,
}: {
  count?: number;
  className?: string;
} & HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex items-center gap-4', className)}>
      {Array.from({ length: count }, (_, i) => (
        <Skeleton key={i} className="h-4 w-20" />
      ))}
    </div>
  );
}
